import * as React from "react";
import { useMemo, useState } from "react";
import { styled } from "@mui/material";
import {
  ReferencesBoxText,
  MobileReasoningTopArea,
  MobileReasoningTopTitle,
} from "./mobileSuggestionReasoningBox";
import { usePatient } from "../../../state/patientContext";
import { useTranslate } from "../../../hooks/useTranslate";
import { findingFormat } from "fe-web-components/formatting/FindingNameFormatter";
import { Citations } from "fe-web-components/citations/Citations";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { GenericButton } from "../../uiControllers/genericButton";
import { DemoZoomContent } from "./useDemoReasoningController";

//region [[ Styles ]]

const MobileReasoningReferencesBoxView = styled("div")({});

const ReferencesContainer = styled("div")({
  marginTop: "10px",
  borderTop: `1px solid ${PatientColorMap.DefaultLight}`,
  "& .resource-count-display": {
    display: "none",
  },
});

const LoadMoreReferencesButton = styled(GenericButton)({});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface MobileReasoningReferencesBoxProps extends DemoZoomContent {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MobileReasoningReferencesBox = ({
  currentSelectedDisease,
  ...props
}: MobileReasoningReferencesBoxProps) => {
  const {
    selectedSuggestion,
    conversation,
    selectedDiseaseReasoning,
  } = usePatient();

  const { diseaseName: selectedDiseaseName, diseaseId: selectedDiseaseId } =
    currentSelectedDisease || {};

  const [currentReferencesTop, setCurrentTop] = useState(100);

  const { translate } = useTranslate();

  const {
    suggestions: { diseases },
  } = conversation || { suggestions: {} };

  const { conversationDDResult, showTriageReasoning } = usePatient();

  const suggestion = conversation?.suggestions?.suggestions?.find(
    (sg) => sg.suggestion.id === selectedSuggestion?.qualifiedFeature?.id,
  );

  const suspectedDiseases = useMemo(
    () =>
      conversation?.suggestions?.diseases.map((d) => ({
        name: d.disease.name,
        id: d.disease.id,
        prob: d.prob,
      })),
    [conversation],
  );

  const diseasesWCrs = diseases?.find(
    (d) =>
      d.disease.id ===
      (selectedDiseaseId
        ? selectedDiseaseId
        : selectedSuggestion?.qualifiedFeature?.id),
  );

  const references = useMemo(() => {
    // In case there are no references found and there is no selected disease,
    // go over all suggestion diseases and collect their references and accumulate
    // them to once object
    if (!diseasesWCrs && !selectedDiseaseId && !selectedDiseaseReasoning) {
      return suspectedDiseases
        ?.flatMap(
          (disease) =>
            diseases?.find((d) => d.disease.id === disease.id)
              ?.diseaseCitations,
        )
        ?.filter((t) => t)
        ?.reduce(
          (acc, current) => ({
            totalCount: (acc?.totalCount || 0) + (current?.totalCount || 0),
            citationsData: [
              ...(acc?.citationsData || []),
              ...(current?.citationsData || []),
            ],
          }),
          { totalCount: 0, citationsData: [] },
        );
    }
    const clusters = [
      ...conversationDDResult?.ddClusters?.top,
      ...conversationDDResult?.ddClusters?.others,
    ];

    if (selectedDiseaseReasoning) {
      return clusters.find(
        (f) => f.title.id === selectedDiseaseReasoning.diseaseId,
      ).cr.diseaseCitations;
    }

    if (showTriageReasoning) {
      return clusters.reduce(
        (acc, cluster) => ({
          totalCount: cluster.cr.diseaseCitations.totalCount + acc.totalCount,
          citationsData: [
            ...acc.citationsData,
            ...cluster.cr.diseaseCitations.citationsData,
          ],
        }),
        {
          totalCount: 0,
          citationsData: [],
        },
      );
    }
    return diseasesWCrs?.diseaseCitations;
  }, [
    showTriageReasoning,
    diseasesWCrs,
    selectedDiseaseId,
    suspectedDiseases,
    diseases,
    selectedDiseaseReasoning,
    conversationDDResult,
  ]);

  const getDiseaseName = () => {
    if (selectedDiseaseName) {
      return <>{selectedDiseaseName}</>;
    }
    if (showTriageReasoning) {
      const { navigationRecommendation } = conversationDDResult;

      return navigationRecommendation?.feature.feature.shortname;
    }
    if (!suggestion) return "";
    return findingFormat(suggestion.bestSubtype || suggestion.suggestion);
  };

  const incReferencesTop = () => setCurrentTop((oldTop) => oldTop * 2);

  return (
    <MobileReasoningReferencesBoxView>
      <MobileReasoningTopArea className={"top-area"}>
        <MobileReasoningTopTitle className={"top-title"}>
          {translate("REFERENCES_IN_CHAT_REASONING_TITLE")
            .split("#DISEASE_NAME#")
            .map((t, idx, arr) => (
              <React.Fragment key={idx}>
                {t} {idx + 1 === arr.length ? null : getDiseaseName()}
              </React.Fragment>
            ))}
        </MobileReasoningTopTitle>
      </MobileReasoningTopArea>
      <ReferencesBoxText>
        {translate("REFERENCES_EXPLANATION_TEXT")}
        {!!references?.totalCount &&
          translate("REFERENCES_FOUND").replace(
            "#REFERENCES_NUMBER#",
            references.totalCount.toFixed(0),
          )}{" "}
        (Showing {Math.min(currentReferencesTop, references?.totalCount || 0)})
      </ReferencesBoxText>
      <ReferencesContainer>
        {!references?.totalCount && <span>{translate("NO_REFERENCES")}</span>}
        <Citations
          citations={{
            ...references,
            citationsData: references?.citationsData?.slice(
              0,
              currentReferencesTop,
            ),
          }}
        />
        {/*TODO: make scrolling intilize this call */}
        {currentReferencesTop < references?.totalCount && (
          <LoadMoreReferencesButton
            onClick={incReferencesTop}
            label={"Load more"}
          />
        )}
      </ReferencesContainer>
    </MobileReasoningReferencesBoxView>
  );
};
